import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import clsx from "clsx";
import DataTable from "components/DataTable";
import { WALLET_PENDING_HEADER_LIST } from "constants/haulpay-broker";
import { usePullToRefresh } from "context/PullToRefreshContext";
import { showErrorMessage } from "helpers/toastMessage";
import useDeviceType from "hooks/useDeviceType";
import { getGenericNextPageAPI, getPendingTransactionAPI } from "services/api/haulpay";
import BrokerWalletPendingItem from "../../../modules/Broker/WalletPendingItem";
import styles from "../History/style.module.scss";
import Button from "components/Button";
import { useUserFeature } from "hooks/useUserFeature";

export default function BrokerWalletPending() {
  const { isDesktop } = useDeviceType();
  const { isWalletEnabled } = useUserFeature();
  const itemsPerPage = 3;
  const [filters, setFilters] = useState<{ orderBy: string }>({ orderBy: "" });
  const pendingQueryKey = ["pendingTransactions", isWalletEnabled, filters.orderBy];

  const {
    data,
    error,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    refetch
  } = useInfiniteQuery(
    pendingQueryKey,
    async ({ pageParam = null, signal, queryKey }) => {
      if (!isWalletEnabled) {
        return { results: [], next: null, previous: null }; // Return empty data structure to avoid undefined errors
      }
      const orderBy = queryKey[2] as string;
      if (pageParam === null) {
        const response = await getPendingTransactionAPI(
          orderBy ? { ordering: orderBy } : undefined,
          signal
        );
        return response.data;
      } else {
        const response = await getGenericNextPageAPI(pageParam, undefined, signal);
        return response.data;
      }
    },
    {
      getNextPageParam: (lastPage: any) => lastPage?.next || undefined,
      getPreviousPageParam: (firstPage: any) => firstPage?.previous || null
    }
  );

  usePullToRefresh(refetch);

  useEffect(() => {
    if (error) {
      showErrorMessage("Failed to fetch pending transactions.");
    }
  }, [error]);

  if (error) {
    return <p>Error loading transactions.</p>;
  }

  const transactionList = data?.pages.flatMap((page: any) => page?.results ?? []) ?? [];
  const totalCount = data?.pages[0]?.count ?? 0;

  const nextLink = data?.pages[data?.pages.length - 1]?.next || null;
  const prevLink = data?.pages[0]?.previous || null;
  const fetchMoreData = async (itemsPerPage: number, pageNumberParam?: number) => {
    try {
      if (itemsPerPage > 0 && hasNextPage) {
        await fetchNextPage();
      } else if (itemsPerPage < 0 && hasPreviousPage) {
        await fetchPreviousPage();
      } else if (pageNumberParam !== undefined) {
        // Handle specific page number if needed
      }
    } catch {
      showErrorMessage("Please try again later.");
    }
  };

  const handleSortBy = (key: string) => {
    setFilters((prev) => {
      return { ...prev, orderBy: key };
    });
  };

  return (
    <div className={styles.walletContainer}>
      <div>
        <div className={clsx(isDesktop && "!hidden")}>
          {transactionList.length > 0 ? (
            transactionList.map((item: any) => (
              <BrokerWalletPendingItem
                key={item.contract_id || `${item.created}-${item.invoice_number}`}
                data={item}
              />
            ))
          ) : (
            <p className="text-center text-placeholder">No Transactions</p>
          )}
        </div>
        <div className={clsx(!isDesktop && "!hidden")}>
          <DataTable
            headers={WALLET_PENDING_HEADER_LIST}
            data={transactionList}
            totalCount={totalCount}
            nextLink={nextLink}
            prevLink={prevLink}
            handlePageChange={fetchMoreData}
            handleSortBy={handleSortBy}
            currentSortKey={filters.orderBy}
            loading={isFetching || isLoading}
          />
        </div>
      </div>
      <div className={styles.paginationButtons}>
        {nextLink && (
          <Button variant="outline" onClick={() => fetchMoreData(itemsPerPage)}>
            {isFetchingNextPage ? "Loading..." : "Next"}
          </Button>
        )}
      </div>
    </div>
  );
}
